import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import {
  selectBestSlotAdjustmentPercent,
  selectPlanVisitFeeCents,
  selectPaymentPreference,
  selectPrepayDiscountPercent,
  selectSlotPrice,
  selectStoredDiscountAmount,
  selectStoredDiscountType,
} from '../features/booking/bookingSlice';
import styles from '../sass/components/BookingSummaryBar.module.scss';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { aperioPlanLabel } from '../util/aperioPlanCopy';
import { serviceDisplayName } from '../util/serviceText';
import { formatDate } from '../i18n/format';
import { zoneSuffix } from '../util/slotTime';
import {
  codeDiscountCents,
  eligibleSubtotalCents,
  formatCents,
  isPlanBooking,
  orderSubtotalCents,
  PaymentPreference,
  prepayDiscountCents,
  withPlanVisitFee,
} from '../util/bookingPricing';
import {
  isSubscribeTo,
  isSubscriptionPlan,
  ServiceNameKeys,
} from '../util/serviceKeys';

/** Strip the "$16.58/Mo – " prefix some plan names carry in the catalogue. */
export const cleanServiceName = (name: string): string => (
  name.replace(/^\$[\d.,]+\s*\/\s*mo\.?\s*[–—-]\s*/i, '').trim()
);

interface FooterSummaryProps {
  title: string;
  when: string | null;
  hasItems: boolean;
  planBooking: boolean;
  items: Parameters<typeof orderSubtotalCents>[0];
  /** The chosen time's adjustment, or null before a time is chosen. */
  slotCents: number | null;
  bestSlotAdjustmentPercent: number;
  prepay: boolean;
  prepayDiscountPercent: number;
  /** The payment choice is made on the schedule step, once a time is set. */
  showPrepay: boolean;
  /** An applied promo code, in cents (0 without one). */
  discountCents?: number;
}

/**
 * The Aperio preset's summary, inside the step's pinned bottom bar. It shows
 * the price BEFORE the prepay discount - the same "due at your first visit"
 * the plan card and the time slots show - and names the prepay price on its
 * own line when prepay is the chosen way to pay. Only `apr-*` classes: styled
 * by public/aperio/theme.css, rendered for no other tenant.
 */
function FooterSummary({
  title,
  when,
  hasItems,
  planBooking,
  items,
  slotCents,
  bestSlotAdjustmentPercent,
  prepay,
  prepayDiscountPercent,
  showPrepay,
  discountCents = 0,
}: FooterSummaryProps) {
  const { t } = useTranslation();
  if (!hasItems) return <div className="apr-dock-summary" />;

  const orderCents = orderSubtotalCents(items);
  const eligibleCents = eligibleSubtotalCents(items);
  let preTax = orderCents;
  let from = false;
  if (slotCents !== null) {
    preTax = orderCents + slotCents;
  } else if (bestSlotAdjustmentPercent < 0 && eligibleCents > 0) {
    preTax = orderCents
      + Math.round((eligibleCents * bestSlotAdjustmentPercent) / 100);
    from = true;
  }
  const prepayCents = preTax + prepayDiscountCents(
    preTax,
    prepayDiscountPercent,
  );
  // One total, the one the chosen way to pay charges (QA 2026-09-25 #21:
  // with prepay chosen the bar led with the pay-at-visit price and the
  // summary with the prepay one). The other price is named below it.
  const prepayLeads = showPrepay && prepay && prepayCents < preTax;
  // A promo code comes off whichever price leads, as on the order summary.
  const headline = (prepayLeads ? prepayCents : preTax) - discountCents;

  return (
    <div className="apr-dock-summary" role="status">
      <span className="apr-dock-summary__price">
        {from && t('summary.fromPrefix')}
        <b>{formatCents(Math.max(0, headline))}</b>
        {t('summary.plusTax')}
        {prepayLeads && (
          <span className="apr-dock-summary__label">
            {t('summary.prepaidOnline')}
          </span>
        )}
        {!prepayLeads && planBooking && (
          <span className="apr-dock-summary__label">
            {t('summary.atFirstVisit')}
          </span>
        )}
      </span>
      <span className="apr-dock-summary__meta">
        {[title, when].filter(Boolean).join(' · ')}
      </span>
      {prepayLeads && (
        <span className="apr-dock-summary__prepay">
          {t('summary.orAtVisit', {
            price: formatCents(Math.max(0, preTax - discountCents)),
          })}
        </span>
      )}
    </div>
  );
}

interface BookingSummaryBarProps {
  /**
   * `top` (default): the strip under the booking steps, as every tenant has
   * it. `footer`: the Aperio preset's version, rendered inside a step's
   * Back / Next bar so that on a phone one bar is pinned, not three.
   */
  placement?: 'top' | 'footer';
}

/**
 * Always-visible price strip under the booking steps. Shows what is in the
 * basket, the price (or "from" price before a time is chosen) and the
 * reminder that booking never requires payment.
 */
export default function BookingSummaryBar({
  placement = 'top',
}: BookingSummaryBarProps = {}) {
  const { t } = useTranslation();
  const services = useAppSelector((state) => state.serviceTypes.services);
  const addOns = useAppSelector((state) => state.addOns.addOns);
  const dateTime = useAppSelector((state) => state.serviceLocation.dateTime);
  const slotPrice = useAppSelector(selectSlotPrice);
  const paymentPreference = useAppSelector(selectPaymentPreference);
  const prepayDiscountPercent = useAppSelector(selectPrepayDiscountPercent);
  const bestSlotAdjustmentPercent = useAppSelector(
    selectBestSlotAdjustmentPercent,
  );
  const storedDiscountAmount = useAppSelector(selectStoredDiscountAmount);
  const storedDiscountType = useAppSelector(selectStoredDiscountType);
  // Class hook for a tenant theme preset's stylesheet. Absent (so the class
  // list is unchanged) for every tenant on the default preset.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';
  // A number, not the config object (a new object on every call would
  // re-render the bar on every store update).
  // The All-Inclusive plan's own fee on an All-Inclusive basket.
  const nextVisitFeeCents = useAppSelector(selectPlanVisitFeeCents);

  const items = [...services, ...addOns];
  const hasItems = services.length > 0;

  const visibleServices = services.filter(
    (s) => !isSubscriptionPlan(s.nameKey)
      && s.nameKey !== ServiceNameKeys.subscriptionPlanAnnualAllInclusive,
  );
  const title = visibleServices
    .map((s) => {
      if (!isSubscribeTo(s.nameKey)) return serviceDisplayName(s);
      // Aperio: the plan card's own short name ("Yearly plan").
      return (themed && aperioPlanLabel(s.nameKey))
        || cleanServiceName(serviceDisplayName(s));
    })
    .join(' + ');

  const orderCents = orderSubtotalCents(items);
  const eligibleCents = eligibleSubtotalCents(items);
  // An applied promo code comes off the price shown, as on the summary.
  const discountCents = codeDiscountCents(
    storedDiscountType,
    storedDiscountAmount,
    items,
  );

  // Every price in the flow is the same "total before tax"; the summary adds
  // the estimated tax line, so the bar just says so once.
  let priceLabel: string;
  if (dateTime && slotPrice) {
    const preTax = orderCents + slotPrice.cents;
    const prepay = paymentPreference === PaymentPreference.prepay
      ? prepayDiscountCents(preTax, prepayDiscountPercent)
      : 0;
    priceLabel = t('summary.priceTax', {
      price: formatCents(Math.max(0, preTax + prepay - discountCents)),
    });
  } else if (bestSlotAdjustmentPercent < 0 && eligibleCents > 0) {
    const best = orderCents
      + Math.round((eligibleCents * bestSlotAdjustmentPercent) / 100);
    priceLabel = t('summary.fromPriceTax', {
      price: formatCents(Math.max(0, best - discountCents)),
    });
  } else {
    priceLabel = t('summary.priceTax', {
      price: formatCents(Math.max(0, orderCents - discountCents)),
    });
  }

  const when = dateTime
    ? formatDate(parseISO(dateTime.date), 'EEE MMM d · h:mm aaa', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) + zoneSuffix(dateTime)
    : null;

  if (placement === 'footer') {
    return (
      <FooterSummary
        title={title}
        when={when}
        hasItems={hasItems}
        planBooking={isPlanBooking(services)}
        // One basket, one figure: the plan card's "due at your first visit"
        // counts the first visit's mobile service fee, which the basket only
        // gets once a time is picked, so it is counted here from the start
        // ($0, so nothing is added, where the onboarding includes it).
        items={withPlanVisitFee(items, nextVisitFeeCents)}
        slotCents={dateTime && slotPrice ? slotPrice.cents : null}
        bestSlotAdjustmentPercent={bestSlotAdjustmentPercent}
        prepay={paymentPreference === PaymentPreference.prepay}
        prepayDiscountPercent={prepayDiscountPercent}
        showPrepay={!!dateTime}
        discountCents={discountCents}
      />
    );
  }

  return (
    <div
      className={cx(styles.bar, { [styles.empty]: !hasItems }, {
        'apr-summary-bar': themed,
        'apr-summary-bar--empty': themed && !hasItems,
      })}
      role="status"
    >
      {hasItems ? (
        <>
          <div className={styles.left}>
            <span className={styles.title}>{title}</span>
            {when && <span className={styles.when}>{when}</span>}
          </div>
          <div className={styles.right}>
            <span className={styles.price}>{priceLabel}</span>
            <span className={styles.note}>
              {paymentPreference === PaymentPreference.prepay
                ? t('summary.prepayOff', { percent: prepayDiscountPercent })
                : t('summary.noPaymentNow')}
            </span>
          </div>
        </>
      ) : (
        <span className={styles.note}>
          <span className={styles.strong}>{t('summary.noPaymentToBook')}</span>
          {' '}
          {t('summary.payAfterOrPrepay', { percent: prepayDiscountPercent })}
        </span>
      )}
    </div>
  );
}
